import { useParams } from 'react-router-dom';
import { useMemo } from 'react';

import { useAppLayoutContextConsumer } from '@occc-app/design-system';
import { formatDateTime, DateTimeFormat } from '@occc-app/lib/date';
import { ScanDataSubmissionType } from '@occc/graphql';

import { buildScanDataPastSubmissionBreadcrumbs } from './breadcrumbs';

import { useScanDataPastSubmissionDetails } from '../../hooks/use-scan-data-past-submission-details.hook';
import {
  SubmissionGroupData,
  PastSubmissionViewData,
} from '../scan-data-weekly-file/scan-data-weekly-file.types';
import { pastSubmissionsTypeToLabelMap } from '../scan-data-weekly-file/scan-data-weekly-file.constants';

type ScanDataPastSubmissionURLParams = {
  date: string;
  type: ScanDataSubmissionType;
};

export const useScanDataPastSubmission = () => {
  const urlParams = useParams<ScanDataPastSubmissionURLParams>();
  const submissionDate = urlParams.date as string;
  const submissionType = urlParams.type as ScanDataSubmissionType;

  const { submission, loading, error } = useScanDataPastSubmissionDetails({
    submissionDate: new Date(submissionDate),
    type: submissionType,
  });

  const submissionData = useMemo((): PastSubmissionViewData | undefined => {
    if (!submission) {
      return undefined;
    }

    const submissions: SubmissionGroupData[] =
      submission.submissionGroupsDetails?.map((group, index) => ({
        visibleId: `${submissionDate}_${group.type}_${index}`,
        name: group.name,
        type: group.type,
        storesSubmittedCount: group.storesSubmittedCount,
        totalStoresCount: group.totalStoresCount,
        fileStatus: group.fileStatus,
        isResubmitted: group.isResubmitted,
        actionsAvailable: group.actionsAvailable,
      })) ?? [];

    return {
      uuid: `${submissionDate}_${submissionType}`,
      weekEndingDate: submissionDate,
      type: submission.type,
      transactionsReported: submission.transactionsReportedCount,
      transactionsReportedChangePercent: submission.transactionsReportedChangePercent,
      submissionGroups: {
        total: submission.submissionGroupsCount,
        submitted: submission.submittedSubmissionGroupsCount,
      },
      fileStatus: submission.fileStatus,
      estimatedReimbursement: submission.estimatedReimbursements,
      submissions,
    };
  }, [submission, submissionDate, submissionType]);

  const pageTitle = submissionData
    ? `Week Ending ${formatDateTime(
        new Date(submissionData.weekEndingDate),
        DateTimeFormat.DateSlashed,
      )}`
    : 'Past Submission View';
  const pageSubtitle = submissionData?.type
    ? pastSubmissionsTypeToLabelMap[submissionData.type]
    : '';

  const breadcrumbLabel = submissionData
    ? `${formatDateTime(
        new Date(submissionData.weekEndingDate),
        DateTimeFormat.DateSlashed,
      )} Submission`
    : 'Past Submission View';

  const breadcrumbs = useMemo(
    () => buildScanDataPastSubmissionBreadcrumbs(breadcrumbLabel),
    [breadcrumbLabel],
  );

  useAppLayoutContextConsumer(breadcrumbs);

  return {
    submissionData,
    pageTitle,
    pageSubtitle,
    loading,
    error,
  };
};
